import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import ReusableBarChart from "../../Components/BarChart/ReusableBarChart";
import ReusablePieChart from "../../Components/PieChart/ReusablePieChart";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Geo,
} from "react-bootstrap-icons";

import styles from "./home.module.css";
import NoData from "../../Components/NoData/NoData";
import toast from "react-hot-toast";
import DateRangePicker from "../../Components/DateRangePicker/DateRangePicker";
import dayjs from "dayjs";

import MostActiveSite from "../../Components/MostActiveSite/MostActiveSite";
import ReusableAreaChart from "../../Components/AreaChart/ReusableAreaChart";
import ProfitChangeCard from "../../Components/ProfitChange/ProfitChangeCard";
import Loading from "../../Components/Loading/Loading";
function Home() {

  const [filters, setFilters] = useState({
    site: { options: [], selected: {} },
    date: {
      selected: {
        from: new Date(new Date().setDate(new Date().getDate() - 30)),
        to: new Date(new Date().setDate(new Date().getDate())),
      },
    },
  });
  const theme = useTheme();
  const [data, setData] = useState(null);
  const allOption = useMemo(() => ({ id: "all", name: "الكل" }), []);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const user = useSelector((state) => state.auth.user);
  const role = useSelector((state) => state.auth.role.roleName);

  const fetchRealtedData = useCallback(async () => {
    setInitialLoading(true);
    try {
      const sites = await axios.get(`${process.env.REACT_APP_API_URL}/sites`, {
        withCredentials: true,
      });

      setFilters((prev) => ({
        ...prev,
        site: {
          options: [allOption, ...sites.data],
          selected: allOption,
        },
      }));
      setInitialLoading(false);
    } catch (error) {
      setInitialLoading(false);
      toast.error("An error occurred");
    }
  }, []);
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        startDate: dayjs(filters.date.selected.from)
          .startOf("day")
          .format("YYYY-MM-DDTHH:mm:ss"),
        endDate: dayjs(filters.date.selected.to)
          .endOf("day")
          .format("YYYY-MM-DDTHH:mm:ss"),
        ...(filters?.site?.selected?.id !== "all" && {
          siteId: filters.site.selected.id,
        }),
      };

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/view/summary`,
        payload,
        {
          withCredentials: true,
        }
      );

      if (res) {
        setData(res.data);      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchRealtedData();
  }, [fetchRealtedData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSelectRange = (range) => {
    setFilters({
      ...filters,
      date: {
        selected: range,
      },
    });
  };

  const handleChangeSiteFilter = (e) => {
    const selectedSite = e.target.value;
    setFilters((prev) => ({
      ...prev,
      site: { ...prev.site, selected: selectedSite },
    }));
  };

  const cardStyle = {
    background: "white",
    padding: "24px",
    display: "flex",
    gap: "20px",
    flexWrap: "wrap",
    borderRadius: "16px",
    width: "fit-content",
    justifyContent: { xs: "center" },
  };
  return (
    <div>
      <Box className={styles.heading}>
        <Typography
          variant="h4"
          color={theme.palette.neutral[900]}
          fontWeight={700}
        >
          {`أهلاً ${user && user.userName}`}
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: { xs: "24px", sm: "12px" },
            justifyContent: "end",
            flexWrap: "wrap",
          }}
        >
          {filters.site.options.length > 0 ? (
            <FormControl
              sx={{ minWidth: "160px", width: { xs: "100%", sm: "160px" } }}
            >
              <InputLabel>المواقع</InputLabel>
              <Select
                value={filters.site.selected}
                label="المواقع"
                onChange={handleChangeSiteFilter}
                renderValue={(selected) => (
                  <Box>
                    <Geo />
                    {selected.name}
                  </Box>
                )}
              >
                {filters.site.options.map((filter, index) => (
                  <MenuItem key={index} value={filter}>
                    {filter.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Skeleton variant="rounded" width={160} height={56} />
          )}
          <DateRangePicker
            range={filters.date.selected}
            handleSelect={handleSelectRange}
          />
        </Box>
      </Box>
      {initialLoading || loading ? (
        <Loading /> 
      ): !data || data.dateCounter === 0 ? (
        <NoData
          title={"لا يوجد معلومات!"}
          message={
            "يرجى تغيير التاريخ أو الموقع المحدد أو إضافة مشاهدات أولاً."
          }
        />
      ) : (
        <Box
          name="Container"
          sx={{
            padding: { xs: "12px", md: "20px", lg: "24px 48px" },
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            textAlign: {xs: "center", sm: "unset"}
          }}
        >
          <Box
            sx={{ ...cardStyle, width: "100%", justifyContent: "space-around" }}
          >
            <Box name="Working-days">
              <Typography variant="h4" fontWeight={600}>
                {data.dateCounter}
              </Typography>
              <Typography>عدد أيام العمل</Typography>
            </Box>
            <Box name="total-views">
              <Typography variant="h4" fontWeight={600}>
                {data.totalQuantity}
              </Typography>
              <Typography>مجموع المشاهدات</Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: "20px",
                flexWrap: "wrap",
                justifyContent: "center",
              }}
              name="total-views-by-types"
            >
              {Object.entries(data?.quantityByViewType || {}).map(
                ([key, value]) => {
                  let viewImage = "Other";
                  if (key === "كراسي الراحة") {
                    viewImage = "Headset";
                  } else if (key === "كراسي المغامرة") {
                    viewImage = "Chair";
                  } else {
                    viewImage = "Other";
                  }
                  return (
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          gap: "8px",
                          alignItems: "center",
                          flexDirection: {xs: "column", sm: "row"}
                        }}
                      >
                        <img src={`/assets/${viewImage}.svg`} alt="icon" />
                        <Typography
                          key={`${key}-1`}
                          variant="h4"
                          fontWeight={600}
                        >
                          {value}
                        </Typography>
                      </Box>
                      <Typography key={key}>{key}</Typography>
                    </Box>
                  );
                }
              )}
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={600}>
                {data.totalAmount}
              </Typography>
              <Typography>مجموع الربح</Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
            <ProfitChangeCard riseFallPercentage={data.riseFallPercentage} />
            <Box
              sx={{
                ...cardStyle,
                width: { xs: "100%", md: "calc(100% - 260px)" },
                padding: "24px 48px",
                gap: "16px",
                justifyContent: { xs: "center", sm: "flex-start" },
              }}
            >
              <Typography variant="h5" fontWeight={600} align="right">
                معدلات
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  justifyContent: { xs: "center", md: "space-between" },
                  flexWrap: "wrap",
                  gap: "20px",
                }}
              >
                <Box name="avg-views">
                  <Typography variant="h4" fontWeight={600}>
                    {data.avgViews}
                  </Typography>
                  <Typography>معدل المشاهدات</Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: "20px",
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  {Object.entries(data?.avgByViewType || {}).map(
                    ([key, value]) => {
                      let viewImage = "Other";
                      if (key === "كراسي الراحة") {
                        viewImage = "Headset";
                      } else if (key === "كراسي المغامرة") {
                        viewImage = "Chair";
                      } else {
                        viewImage = "Other";
                      }
                      return (
                        <Box name="avg-views-by-types">
                          <Box
                            sx={{
                              display: "flex",
                              gap: "8px",
                              alignItems: "center",
                              flexDirection: { xs: "column", sm: "row" },
                            }}
                          >
                            <img src={`/assets/${viewImage}.svg`} alt="icon" />
                            <Typography
                              key={`${key}-1`}
                              variant="h4"
                              fontWeight={600}
                            >
                              {value}
                            </Typography>
                          </Box>
                          <Typography key={key}>معدل {key}</Typography>
                        </Box>
                      );
                    }
                  )}
                </Box>
                <Box name="amount-avg">
                  <Typography variant="h4" fontWeight={600}>
                    {data.avgAmount}
                  </Typography>
                  <Typography>معدل الربح</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
            <Box
              sx={{
                ...cardStyle,
                flexDirection: "column",
                width: { xs: "100%", sm: "100%", md: "30%" },
              }}
            >
              <Typography variant="h5" fontWeight={600}>
                طرق الدفع المستخدمة
              </Typography>
              <ReusablePieChart data={data.paymentMethodPie} />
            </Box>
            {data.areaChartData && data.areaChartData.length > 1 ? (
              <ReusableAreaChart
                data={data.areaChartData}
                title={"العائدات المالية"}
                width={"calc(70% - 20px )"}
              />
            ) : (
              ""
            )}
          </Box>
          <Box sx={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
            <MostActiveSite site={data.mostActiveSite} />
            <ReusableBarChart
              data={data.barChartData}
              xAxisKey={data.barChartXAxisKey}
              title={"توزع المشاهدات"}
            />
          </Box>
        </Box>
      )}
    </div>
  );
}

export default Home;
